/**
 * Voice Input Bar Component
 * ==========================
 * Real-time voice input that directly fills chart data as doctors speak
 */

import { useEffect, useState } from 'react';
import { Mic, Volume2, CheckCircle, AlertCircle, Square } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useVoiceInput, parseVoiceInput, ParsedVoiceData } from '@/hooks/useVoiceInput';
import toast from 'react-hot-toast';

interface VoiceInputBarProps {
  onDataParsed: (data: ParsedVoiceData) => void;
  isEnabled: boolean;
  onToggle: () => void;
}

export function VoiceInputBar({ onDataParsed, isEnabled, onToggle }: VoiceInputBarProps) {
  console.log('[VoiceInputBar] Render - isEnabled:', isEnabled);
  
  const [parsedData, setParsedData] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    startListening,
    stopListening,
  } = useVoiceInput({
    continuous: true,
    interimResults: true,
    language: 'en-US',
    onResult: (finalText) => {
      console.log('Final transcript:', finalText);
      
      // Try to parse any voice input data
      const parsed = parseVoiceInput(finalText);
      
      if (parsed) {
        console.log('Parsed data:', parsed);
        onDataParsed(parsed);
        
        // Show success feedback based on data type
        let feedbackMessage = '';
        
        switch (parsed.type) {
          case 'site_readings': {
            const readingsSummary = parsed.readings
              .map(r => `${r.site}: ${r.pocketDepth ?? '-'}${r.bleeding ? ' (bleeding)' : ''}`)
              .join(', ');
            feedbackMessage = `Tooth ${parsed.toothNumber}: ${readingsSummary}`;
            setParsedData(feedbackMessage);
            toast.success(`Recorded: Tooth ${parsed.toothNumber}`, {
              icon: '✓',
              duration: 2000,
            });
            break;
          }
          case 'clinical_notes':
            feedbackMessage = `Clinical Notes: ${parsed.notes.substring(0, 50)}${parsed.notes.length > 50 ? '...' : ''}`;
            setParsedData(feedbackMessage);
            toast.success('Clinical notes recorded', {
              icon: '📝',
              duration: 2000,
            });
            break;
          case 'aap_classification': {
            const parts = [];
            if (parsed.stage) parts.push(parsed.stage);
            if (parsed.grade) parts.push(parsed.grade);
            if (parsed.extent) parts.push(parsed.extent);
            feedbackMessage = `AAP: ${parts.join(', ')}`;
            setParsedData(feedbackMessage);
            toast.success('AAP classification recorded', {
              icon: '📊',
              duration: 2000,
            });
            break;
          }
          case 'risk_factor':
            feedbackMessage = `Risk Factor: ${parsed.factor.replace('_', ' ')} ${parsed.enabled ? 'added' : 'removed'}`;
            setParsedData(feedbackMessage);
            toast.success(feedbackMessage, {
              icon: parsed.enabled ? '⚠️' : '✓',
              duration: 2000,
            });
            break;
          case 'referral':
            if (parsed.reason) {
              feedbackMessage = `Referral: ${parsed.reason.substring(0, 50)}${parsed.reason.length > 50 ? '...' : ''}`;
            } else {
              feedbackMessage = `Referral: ${parsed.recommended ? 'Recommended' : 'Not recommended'}`;
            }
            setParsedData(feedbackMessage);
            toast.success(parsed.recommended ? 'Referral recommended' : 'Referral removed', {
              icon: parsed.recommended ? '🏥' : '✓',
              duration: 2000,
            });
            break;
          case 'tooth_status':
            feedbackMessage = `Tooth ${parsed.toothNumber}: ${parsed.status.replace('_', ' ')}`;
            setParsedData(feedbackMessage);
            toast.success(`Tooth ${parsed.toothNumber} marked as ${parsed.status.replace('_', ' ')}`, {
              icon: '🦷',
              duration: 2000,
            });
            break;
          case 'mobility':
            feedbackMessage = `Tooth ${parsed.toothNumber}: Mobility ${parsed.mobility ?? 0}`;
            setParsedData(feedbackMessage);
            toast.success(`Mobility ${parsed.mobility ?? 0} recorded for tooth ${parsed.toothNumber}`, {
              icon: '📏',
              duration: 2000,
            });
            break;
          case 'furcation':
            feedbackMessage = `Tooth ${parsed.toothNumber}: Furcation ${parsed.furcation ?? 0}`;
            setParsedData(feedbackMessage);
            toast.success(`Furcation ${parsed.furcation ?? 0} recorded for tooth ${parsed.toothNumber}`, {
              icon: '🔍',
              duration: 2000,
            });
            break;
        }
        
        setShowFeedback(true);
        setTimeout(() => setShowFeedback(false), 3000);
      } else {
        console.log('Could not parse data from:', finalText);
        // Show what we heard but couldn't parse
        if (finalText.trim().length > 0) {
          toast.error('Could not understand command. Try: "Tooth 14 MB 3" or "Tooth 8 missing"', {
            duration: 3000,
          });
        }
      }
    },
  });

  // Toggle listening when enabled state changes
  useEffect(() => {
    console.log('[VoiceInputBar] useEffect triggered', { 
      isEnabled, 
      isListening, 
      hasError: !!error,
      errorMessage: error,
      isSupported 
    });
    
    if (!isSupported) {
      console.log('[VoiceInputBar] Voice input not supported in this browser');
      return;
    }
    
    // Only act if there's a state mismatch
    if (isEnabled && !isListening) {
      if (error) {
        console.log('[VoiceInputBar] Not starting due to error:', error);
        return;
      }
      
      console.log('[VoiceInputBar] Mismatch: enabled but not listening - Starting in 300ms...');
      // Small delay before restart to prevent overwhelming the speech API
      const timeoutId = setTimeout(() => {
        console.log('[VoiceInputBar] Executing delayed start');
        startListening();
      }, 300);
      
      return () => {
        console.log('[VoiceInputBar] Cleanup: clearing timeout');
        clearTimeout(timeoutId);
      };
    } else if (!isEnabled && isListening) {
      console.log('[VoiceInputBar] Mismatch: disabled but listening - Stopping...');
      stopListening();
    } else {
      console.log('[VoiceInputBar] States match - no action needed');
    }
    // Only depend on isEnabled and isListening, not the functions to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, isListening, error]);

  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(error, { duration: 4000 });
      if (isEnabled) {
        onToggle(); // Turn off voice input on error
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (!isSupported) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 text-yellow-800 text-sm rounded-md">
        <AlertCircle className="w-4 h-4" />
        Voice input not supported in this browser
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {/* Voice Toggle/Stop Button */}
      {isListening ? (
        <Button
          variant="danger"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const timestamp = Date.now();
            console.log('[VoiceInputBar] ========== STOP BUTTON CLICKED ==========');
            console.log('[VoiceInputBar] Timestamp:', timestamp);
            console.log('[VoiceInputBar] Current state - isEnabled:', isEnabled, 'isListening:', isListening);
            console.log('[VoiceInputBar] Calling onToggle()');
            onToggle();
            console.log('[VoiceInputBar] onToggle() called');
          }}
          className="animate-pulse"
          type="button"
        >
          <Square className="w-4 h-4 mr-2 pointer-events-none" />
          <span className="pointer-events-none">Stop Listening</span>
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const timestamp = Date.now();
            console.log('[VoiceInputBar] ========== START BUTTON CLICKED ==========');
            console.log('[VoiceInputBar] Timestamp:', timestamp);
            console.log('[VoiceInputBar] Current state - isEnabled:', isEnabled, 'isListening:', isListening);
            console.log('[VoiceInputBar] Error state:', error);
            console.log('[VoiceInputBar] isSupported:', isSupported);
            console.log('[VoiceInputBar] Calling onToggle()');
            onToggle();
            console.log('[VoiceInputBar] onToggle() called');
          }}
          type="button"
        >
          <Mic className="w-4 h-4 mr-2 pointer-events-none" />
          <span className="pointer-events-none">Voice charting</span>
        </Button>
      )}

      {/* Live Transcript Display */}
      {isListening && (
        <>
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-200 max-w-md">
            <Volume2 className="w-4 h-4 text-blue-600 flex-shrink-0 pointer-events-none" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-blue-900 truncate pointer-events-none">
                {interimTranscript || transcript || 'Speak now...'}
              </p>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 h-3 bg-blue-500 rounded-full animate-wave pointer-events-none"
                  style={{
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
          </div>
          
          {/* Active Indicator */}
          <div className="flex items-center gap-1.5 text-sm text-blue-600">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse pointer-events-none" />
            <span className="font-medium pointer-events-none">Active</span>
          </div>
        </>
      )}

      {/* Success Feedback */}
      {showFeedback && parsedData && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-md border border-green-200">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-900">{parsedData}</p>
        </div>
      )}
    </div>
  );
}
